import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Routes, Route, useLocation } from "react-router-dom"

// Import Routes all
import { authProtectedRoutes, publicRoutes } from "./routes"
// Import all middleware
import Authmiddleware from "./routes/route"

// layouts Format
import VerticalLayout from "./components/VerticalLayout/"
import NonAuthLayout from "./components/NonAuthLayout"
import "./assets/scss/theme.scss"
import "./assets/css/generalCss.css"
import AuthState from "./context/auth/authState"
import ContactState from "context/contact/contactState"
import ProjectState from "./context/projects/projectState"
import FileState from "context/file/fileState"
import TeamsState from "context/teams/teamsState"
import ActsState from "context/acts/actsState"
import BankPricesState from "context/bankPrices/bankPricesState"
import BankPricesReferenceState from "context/referenceBankPrices/bankPricesReferenceState"
import TemplatesState from "context/templates/templatesState"
import FavoritesBPState from "context/favoritesBP/favoritesBPState"
import FacturasState from "context/facturas/facturasState"
import { SnackbarProvider } from "notistack"
import { AwsRum } from "aws-rum-web"
import Budget2023State from "context/budget2023/Budget2023State"
import * as amplitude from "@amplitude/analytics-browser"
import CertHistoryState from "context/certHistory/CertHistoryState"

import SubscriptionProvider from "context/subscription/subscriptionProvider"
import WrapperModals from "components/AppModals/WrapperModals"
import { createTheme, ThemeProvider } from "@mui/material/styles"

import { THEME_OPTIONS } from "./theme"

const theme = createTheme(THEME_OPTIONS)

let awsRum = null
try {
  const config = {
    sessionSampleRate: 1,
    guestRoleArn: process.env.REACT_APP_AWS_RUM_GUEST_ROLE_ARN,
    identityPoolId: process.env.REACT_APP_AWS_RUM_IDENTITY_POOL_ID,
    endpoint: process.env.REACT_APP_AWS_RUM_ENDPOINT,
    telemetries: ["performance", "errors", "http"],
    allowCookies: true,
    enableXRay: false,
  }

  const APPLICATION_ID = process.env.REACT_APP_AWS_RUM_APPLICATION_ID
  const APPLICATION_VERSION = process.env.REACT_APP_AWS_RUM_APPLICATION_VERSION
  const APPLICATION_REGION = process.env.REACT_APP_AWS_RUM_APPLICATION_REGION

  awsRum = new AwsRum(
    APPLICATION_ID,
    APPLICATION_VERSION,
    APPLICATION_REGION,
    config,
  )
} catch (error) {
  // Ignore errors thrown during CloudWatch RUM web client initialization
}

export function RecordPageView() {
  let location = useLocation()
  React.useEffect(() => {
    awsRum.recordPageView(location.pathname)
  }, [location])
}

export function RecordPageViewWithoutUserId() {
  let location = useLocation()
  let baseLocation = location.pathname.split("/")
  baseLocation.pop()
  const baseLocationPath = baseLocation.join("/")
  React.useEffect(() => {
    awsRum.recordPageView(baseLocationPath)
  }, [baseLocationPath])
}
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }
  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    awsRum.recordError(error)
  }
  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.children
    }
    return this.props.children
  }
}
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

import { LicenseInfo } from "@mui/x-license"
import ExtrasAndContradictionsState from "context/extrasAndContradictions/ExtrasAndContradictionsState"
import WrapperModalsProvider from "context/wrapperModals/WrapperModalsProvider"
import TemplatesManagerState from "context/templates/TemplatesManagerState"
import { DynamicElementsProvider } from "context/dynamicElements/DynamicElementsContext"

const App = props => {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY)

  const Layout = VerticalLayout

  useEffect(() => {
    amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, null, {
      defaultTracking: {
        pageViews: false,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
      },
    })
  }, [])

  const [queryClient] = useState(() => new QueryClient())
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider maxSnack={3}>
          <AuthState>
            <SubscriptionProvider>
              <ProjectState>
                <FileState>
                  <ContactState>
                    <WrapperModalsProvider>
                      <ExtrasAndContradictionsState>
                        <Budget2023State>
                          <CertHistoryState>
                            <TeamsState>
                              <ActsState>
                                <BankPricesState>
                                  <BankPricesReferenceState>
                                    <TemplatesState>
                                      <TemplatesManagerState>
                                        <FavoritesBPState>
                                          <FacturasState>
                                            <DynamicElementsProvider>
                                              <ErrorBoundary>
                                                <Routes>
                                                  {publicRoutes.map(
                                                    (route, idx) => (
                                                      <Route
                                                        path={route.path}
                                                        element={
                                                          <Authmiddleware
                                                            layout={
                                                              NonAuthLayout
                                                            }
                                                            component={
                                                              route.component
                                                            }
                                                            isAuthProtected={
                                                              false
                                                            }
                                                          />
                                                        }
                                                        key={"public-" + idx}
                                                      />
                                                    ),
                                                  )}

                                                  {authProtectedRoutes.map(
                                                    (route, idx) => (
                                                      <Route
                                                        path={route.path}
                                                        element={
                                                          <Authmiddleware
                                                            layout={Layout}
                                                            component={
                                                              route.component
                                                            }
                                                            navContent={
                                                              route.NavContent
                                                            }
                                                            isAuthProtected={
                                                              true
                                                            }
                                                          />
                                                        }
                                                        key={"auth-" + idx}
                                                      />
                                                    ),
                                                  )}
                                                </Routes>
                                              </ErrorBoundary>
                                              <WrapperModals />
                                            </DynamicElementsProvider>
                                          </FacturasState>
                                        </FavoritesBPState>
                                      </TemplatesManagerState>
                                    </TemplatesState>
                                  </BankPricesReferenceState>
                                </BankPricesState>
                              </ActsState>
                            </TeamsState>
                          </CertHistoryState>
                        </Budget2023State>
                      </ExtrasAndContradictionsState>
                    </WrapperModalsProvider>
                  </ContactState>
                </FileState>
              </ProjectState>
            </SubscriptionProvider>
          </AuthState>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

export default App
